import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import {
  NbButtonModule,
  NbCardModule,
  NbDialogRef,
  NbSpinnerModule,
  NbToastrService,
} from '@nebular/theme';
import { finalize } from 'rxjs/operators';
import { Turma } from '../../../core/api/turma';
import { TurmaCatequizandoService } from '../../../core/api/turma-catequizando.service';
import { TurmaService } from '../../../core/api/turma.service';
import { AutocompleteComponent, OpcaoAutocomplete } from '../../../shared/autocomplete/autocomplete.component';
import { mensagemDeErro } from '../../../utils/mensagem-erro';

/**
 * Efetiva a matrícula de quem se pré-inscreveu: escolhe a turma e chama
 * `POST /turmas-catequizandos/matricular`.
 *
 * Não existe endpoint de conversão — a pré-inscrição não vira matrícula sozinha. O que há é a
 * pessoa já criada no cadastro (`pessoaId` vem no pré-inscrito), e é ela que se matricula.
 */
@Component({
  selector: 'app-efetivar-matricula-dialog',
  standalone: true,
  imports: [CommonModule, NbCardModule, NbButtonModule, NbSpinnerModule, AutocompleteComponent],
  template: `
    <nb-card class="dialog-card efetivar-matricula">
      <nb-card-header>
        <span class="titulo">Efetivar matrícula</span>
        <span class="subtitulo">{{ pessoaNome }}</span>
      </nb-card-header>

      <nb-card-body>
        <app-autocomplete
          rotulo="Turma de destino"
          placeholder="Digite parte do nome ou o ano"
          [opcoes]="opcoesTurma"
          [selecionadoId]="turmaSelecionada"
          (selecionadoIdChange)="turmaSelecionada = $event"
          [buscaNoServidor]="true"
          [carregando]="carregandoTurmas"
          (buscar)="carregarTurmas($event)"
          msgSemResultado="Nenhuma turma encontrada"
        ></app-autocomplete>

        <p class="ajuda">
          A matrícula é criada na turma escolhida. O grupo da pré-inscrição é da faixa etária e não
          define turma.
        </p>
      </nb-card-body>

      <nb-card-footer class="footer-actions">
        <button nbButton status="basic" size="medium" type="button" (click)="fechar()">Cancelar</button>
        <button
          nbButton
          status="primary"
          size="medium"
          type="button"
          [disabled]="!turmaSelecionada || salvando"
          [nbSpinner]="salvando"
          (click)="matricular()"
        >
          Matricular
        </button>
      </nb-card-footer>
    </nb-card>
  `,
  styles: [
    `
      .efetivar-matricula nb-card-header {
        display: flex;
        flex-direction: column;
        gap: 0.2rem;
      }

      .titulo {
        font-weight: 600;
      }

      .subtitulo {
        font-size: 0.82rem;
        font-weight: 400;
        color: var(--text-hint-color, #8f9bb3);
        word-break: break-word;
      }

      .ajuda {
        margin: 1.75rem 0 0;
        font-size: 0.82rem;
        color: var(--text-hint-color, #8f9bb3);
      }
    `,
  ],
})
export class EfetivarMatriculaDialogComponent {
  @Input() pessoaId = '';
  @Input() pessoaNome = '';

  turmas: Turma[] = [];
  turmaSelecionada = '';
  carregandoTurmas = false;
  salvando = false;

  constructor(
    private ref: NbDialogRef<EfetivarMatriculaDialogComponent>,
    private turmaService: TurmaService,
    private matriculaService: TurmaCatequizandoService,
    private toastr: NbToastrService,
  ) {
    this.carregarTurmas();
  }

  /** `FRM-04` — o termo vai para o servidor; não é filtro sobre uma página carregada. */
  carregarTurmas(termo = ''): void {
    this.carregandoTurmas = true;
    this.turmaService
      .listar(0, 20, termo)
      .pipe(finalize(() => (this.carregandoTurmas = false)))
      .subscribe({
        next: (res) => (this.turmas = res.content ?? []),
        error: (err) =>
          this.toastr.danger(mensagemDeErro(err, 'Não foi possível carregar as turmas.'), 'Erro'),
      });
  }

  get opcoesTurma(): OpcaoAutocomplete[] {
    return this.turmas.map((t) => {
      const nome = (t.nome ?? '').trim();
      const ano = t.ano != null ? String(t.ano) : '';
      return { id: t.id ?? '', rotulo: !ano || nome.includes(ano) ? nome : `${nome} (${ano})` };
    });
  }

  matricular(): void {
    if (!this.turmaSelecionada || !this.pessoaId || this.salvando) return;

    this.salvando = true;
    this.matriculaService
      .matricular(this.turmaSelecionada, this.pessoaId)
      .pipe(finalize(() => (this.salvando = false)))
      .subscribe({
        next: () => {
          this.toastr.success(`${this.pessoaNome} matriculado(a).`, 'Matrícula');
          this.ref.close(true);
        },
        error: (err) =>
          this.toastr.danger(mensagemDeErro(err, 'Não foi possível efetivar a matrícula.'), 'Erro'),
      });
  }

  fechar(): void {
    this.ref.close(false);
  }
}
